import { queryOptions } from "@tanstack/react-query";
import { myPlayersKey } from "@/features/players/records";
import { readAccountProfile } from "./account.api";
import { getAccountPage } from "./account.functions";
import type { AccountPageData } from "./account.types";

export type { AccountUser } from "./account.types";

export function accountProfileKey(viewerId: string) {
  return ["account", "profile", viewerId] as const;
}

export function accountProfileQueryOptions({ viewerId }: { viewerId: string }) {
  return queryOptions({
    queryKey: accountProfileKey(viewerId),
    queryFn: ({ signal }) =>
      readAccountProfile({ viewerId, signal }).then(({ user }) => user),
    staleTime: 30_000,
  });
}

export function accountPageQueryOptions() {
  return queryOptions({
    queryKey: ["account", "page"],
    queryFn: async ({ client, signal }): Promise<AccountPageData> => {
      const updatedAt = Date.now();
      const seed = await getAccountPage({ signal });
      signal.throwIfAborted();

      const viewerId = seed.sessionUserId;
      const profileKey = accountProfileKey(viewerId);
      const { user, ...page } = seed;

      if (!client.getQueryState(profileKey))
        client.setQueryData(profileKey, user, { updatedAt });

      if ("players" in page) {
        const { players, ...profilePage } = page;
        const playersKey = myPlayersKey({
          viewerId,
          elevatedPrivileges: false,
        });

        if (!client.getQueryState(playersKey))
          client.setQueryData(playersKey, players, { updatedAt });

        return profilePage;
      }

      return page;
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}
